import { getAccessToken, renewSession as renewSharedSession, SessionError } from '@cobliteam/api/session';
import type { AccessibleFleet, Session } from '@cobliteam/common-types';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import type { AxiosError } from 'axios';
import snakecaseKeys from 'snakecase-keys';
import { CobliAxios } from '../../request/CobliAxios';
import HttpMethod from '../../request/HttpMethod';
import HttpStatusCode from '../../request/HttpStatusCode';
import type { Configuration, IRequest } from '../../request/Types';
import type { SessionAPI, UserInfoUpdateRequestAPI } from './ApiTypes';
import { deleteAuthCookies, setAuthCookies } from './AuthToken';
import { transformSession } from './legacySessionTransformer';

const parseAndStoreSession = (sessionAPI: SessionAPI): Session => {
  const session = transformSession(sessionAPI);
  setAuthCookies(session.id, session.authenticationToken);
  return session;
};

// A renovação saiu do CobliAxios para a primitiva de `@cobliteam/api/session` (ADR-0021, Etapa 15), que mantém
// um voo único para o painel inteiro. O CobliAxios reportava a falha ao Datadog e recarregava a página no 401;
// a ponte mantém os dois, porque a primitiva só apaga a sessão e rejeita.
// No voo único, os chamadores simultâneos recebem o mesmo erro; o CobliAxios reportava e recarregava uma vez só.
const handledFailures = new WeakSet<SessionError>();

const reportRenewalFailure = (error: unknown): never => {
  if (error instanceof SessionError && error.reason === 'http' && !handledFailures.has(error)) {
    handledFailures.add(error);
    const datadogError = new Error(`GET /account/session failed - ${error.status}`);
    datadogError.name = 'CobliAxiosError';
    DatadogTrack.error(datadogError, { endpoint: '/account/session', method: 'GET', status: error.status, EventSource: 'CobliAxios' });
    if (error.status === HttpStatusCode.UNAUTHORIZED) window.location.reload();
  }
  throw error;
};

export class SessionManager {
  private axiosInstance: CobliAxios;

  constructor(configuration: Configuration, axiosInstance: CobliAxios = new CobliAxios(configuration)) {
    this.axiosInstance = axiosInstance;
  }

  private _handleKnownErrors(error: AxiosError) {
    const { response } = error;
    if (response) {
      // The request was made and the server responded with a status code

      const httpStatus = response.status;
      if (httpStatus === HttpStatusCode.PRECONDITION_REQUIRED) {
        const session = response.data as SessionAPI;
        return parseAndStoreSession(session);
      }
      if (httpStatus === HttpStatusCode.UNAUTHORIZED || httpStatus === HttpStatusCode.FORBIDDEN) {
        deleteAuthCookies();
      }
    }
    throw error;
  }

  public login = (rawUsername: string, password: string): Promise<Session> => {
    const username = rawUsername.trim().toLocaleLowerCase();
    const requestData: IRequest = {
      endpoint: '/account/authenticate',
      method: HttpMethod.POST,
      withCredentials: true,
      body: JSON.stringify({
        username,
        password,
      }),
    };
    return this.axiosInstance
      .request(requestData)
      .then((response) => parseAndStoreSession(response.data))
      .catch(this._handleKnownErrors);
  };

  public tokenExchange = (idToken: string): Promise<Session> => {
    const requestData: IRequest = {
      endpoint: '/account/token-exchange',
      method: HttpMethod.POST,
      withCredentials: true,
      body: JSON.stringify({
        id_token: idToken,
      }),
    };
    return this.axiosInstance
      .request(requestData)
      .then((response) => parseAndStoreSession(response.data))
      .catch(this._handleKnownErrors);
  };

  public logout = (): Promise<boolean> => {
    const requestData: IRequest = {
      endpoint: '/account/logout',
      method: HttpMethod.POST,
    };
    return this.axiosInstance
      .request(requestData)
      .then(deleteAuthCookies)
      .then(() => true);
  };

  /** Renova já e devolve o access token novo, no voo único da primitiva. */
  public renewSession = (): Promise<string> => renewSharedSession().catch(reportRenewalFailure);

  /** Access token válido por mais de 120 s, renovando antes quando precisa. */
  public maybeRenewAccessToken = (): Promise<string> => getAccessToken().catch(reportRenewalFailure);

  public getSession = (): Promise<Session> => {
    const requestData: IRequest = {
      endpoint: '/account/session',
      method: HttpMethod.GET,
      withCredentials: true,
    };
    return this.axiosInstance
      .request(requestData)
      .then((response) => parseAndStoreSession(response.data))
      .catch(this._handleKnownErrors);
  };

  public update = (updates: UserInfoUpdateRequestAPI): Promise<Session> => {
    const requestData: IRequest = {
      endpoint: '/account/update',
      method: HttpMethod.POST,
      body: JSON.stringify(updates),
    };
    return this.axiosInstance
      .request(requestData)
      .then((response) => parseAndStoreSession(response.data))
      .catch(this._handleKnownErrors);
  };

  public changeUserLoggedFleet = (userFleet: AccessibleFleet): Promise<Session> => {
    const requestData: IRequest = {
      endpoint: `/account/session/${userFleet.id}`,
      method: HttpMethod.POST,
    };
    return this.axiosInstance
      .request(requestData)
      .then((response) => parseAndStoreSession(response.data))
      .catch(this._handleKnownErrors);
  };

  public getHubSpotSSOToken = async (): Promise<string> => {
    const requestData: IRequest = {
      endpoint: '/account/subscription-manager-token',
      method: HttpMethod.GET,
      withCredentials: true,
    };

    const response = await this.axiosInstance.request(requestData);
    const { jwt } = response.data;
    return jwt;
  };

  public getIntercomJWT = async (): Promise<string> => {
    const requestData: IRequest = {
      endpoint: '/account/intercom-jwt',
      method: HttpMethod.GET,
      withCredentials: true,
    };

    const response = await this.axiosInstance.request(requestData);
    const { jwt } = response.data;
    return jwt;
  };

  public getTemporaryToken = async (fleetId: string, fleetName: string): Promise<string> => {
    const requestData: IRequest = {
      endpoint: '/account/temporary-fleet-token',
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys({ fleetId, fleetName })),
    };

    const response = await this.axiosInstance.request(requestData);

    return response.data;
  };
}

export default SessionManager;
